import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Phone } from 'lucide-react';

interface PhoneNumber {
  phoneNumber: string;
  description: string | null;
}

interface PhoneSelectorProps {
  phoneNumbers: PhoneNumber[];
  selectedPhone: string | undefined;
  onSelect: (phone: string) => void;
  unreadCounts: { [key: string]: number };
  formatPhoneNumber: (phone: string) => string;
}

export function PhoneSelector({
  phoneNumbers,
  selectedPhone,
  onSelect,
  unreadCounts,
  formatPhoneNumber
}: PhoneSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const selectedPhoneObj = phoneNumbers.find(p => p.phoneNumber === selectedPhone);
  const selectedDisplay = selectedPhoneObj?.description || (selectedPhone ? formatPhoneNumber(selectedPhone) : 'Select phone');
  const selectedUnread = selectedPhone ? unreadCounts[selectedPhone] || 0 : 0;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center justify-between w-full h-10 px-3 rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent/50 hover:border-accent-foreground/20 min-w-[220px] max-w-[320px] group gap-2"
        title={selectedPhone ? formatPhoneNumber(selectedPhone) : ''}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Phone className="h-4 w-4 flex-shrink-0 text-muted-foreground group-hover:text-foreground transition-colors" />
          <span className="truncate">{selectedDisplay}</span>
          {selectedUnread > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-primary-foreground bg-primary rounded-full flex-shrink-0">
              {selectedUnread}
            </span>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-muted-foreground group-hover:text-foreground transition-all duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full min-w-[280px] rounded-md border border-input bg-popover shadow-lg">
          <div className="p-1 max-h-[300px] overflow-y-auto">
            {phoneNumbers.map((phone) => {
              const isSelected = phone.phoneNumber === selectedPhone;
              const unreadCount = unreadCounts[phone.phoneNumber] || 0;
              const displayText = phone.description || formatPhoneNumber(phone.phoneNumber);

              return (
                <button
                  key={phone.phoneNumber}
                  onClick={() => {
                    onSelect(phone.phoneNumber);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-all duration-150 relative group/item ${
                    isSelected
                      ? 'bg-accent/70 text-accent-foreground font-medium shadow-sm'
                      : 'hover:bg-accent/50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className={`font-medium truncate ${isSelected ? '' : 'group-hover/item:text-foreground'}`}>
                        {displayText}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 group-hover/item:text-muted-foreground/80">
                        {formatPhoneNumber(phone.phoneNumber)}
                      </div>
                    </div>
                    {unreadCount > 0 && (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-primary-foreground bg-primary rounded-full flex-shrink-0">
                        {unreadCount}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

